/**
 * Tutoriel interactif Perē Fa'anu'u — étapes guidées.
 */
import { SIZE } from "./engine";

function emptyBoard() {
  return Array.from({ length: SIZE }, () => Array(SIZE).fill(null));
}

function sameCell(a, b) {
  return a[0] === b[0] && a[1] === b[1];
}

function moveMatches(m, from, to, minCaptures = 0) {
  return (
    sameCell(m.from, from) &&
    sameCell(m.to, to) &&
    (m.captures?.length || 0) >= minCaptures
  );
}

/** @typedef {'info' | 'play'} TutorialStepType */

/**
 * @typedef {Object} TutorialStep
 * @property {string} id
 * @property {TutorialStepType} type
 * @property {string} title
 * @property {string} instruction
 * @property {() => any[][]} [getBoard]
 * @property {'A' | 'B'} [turn]
 * @property {(move: object) => boolean} [validateMove]
 * @property {string} [success]
 */

/** @type {TutorialStep[]} */
export const TUTORIAL_STEPS = [
  {
    id: "welcome",
    type: "info",
    title: "Bienvenue !",
    instruction:
      "Le Perē Fa'anu'u se joue sur 25 intersections (5×5). Les blancs partent du bas et montent ; les noirs descendent. Chaque case est repérée par (colonne, ligne), de (0,0) en bas à gauche à (4,4) en haut à droite.",
  },
  {
    id: "board",
    type: "info",
    title: "Le plateau",
    instruction:
      "Les pions se déplacent le long des lignes : avant, côtés et diagonales avant. Un pion normal ne recule jamais. Les points orange sur le plateau indiquent où vous pouvez aller après avoir sélectionné un pion.",
  },
  {
    id: "move",
    type: "play",
    title: "Étape 1 — Déplacer",
    instruction:
      "Cliquez sur votre pion blanc en (2, 1), puis sur (2, 2) pour l'avancer d'une case vers le haut.",
    getBoard: () => {
      const b = emptyBoard();
      b[1][2] = { owner: "A", king: false };
      return b;
    },
    turn: "A",
    validateMove: (m) => moveMatches(m, [2, 1], [2, 2]),
    success: "Parfait ! Les blancs avancent vers le haut du plateau.",
  },
  {
    id: "capture",
    type: "play",
    title: "Étape 2 — Capturer (Amu)",
    instruction:
      "Sautez par-dessus le pion noir : sélectionnez votre pion en (2, 0) et allez en (2, 2) pour le prendre.",
    getBoard: () => {
      const b = emptyBoard();
      b[0][2] = { owner: "A", king: false };
      b[1][2] = { owner: "B", king: false };
      return b;
    },
    turn: "A",
    validateMove: (m) => moveMatches(m, [2, 0], [2, 2], 1),
    success: "Excellent ! Vous avez capturé un pion adverse en sautant par-dessus.",
  },
  {
    id: "must-capture",
    type: "play",
    title: "Étape 3 — Capture obligatoire",
    instruction:
      "Quand une prise est possible, vous devez la jouer (pions cerclés en orange). Prenez le noir en (3, 2) avec votre pion en (1, 2).",
    getBoard: () => {
      const b = emptyBoard();
      b[2][1] = { owner: "A", king: false };
      b[2][2] = { owner: "B", king: false };
      return b;
    },
    turn: "A",
    validateMove: (m) => moveMatches(m, [1, 2], [3, 2], 1),
    success: "Bien joué ! La capture est toujours prioritaire sur un simple déplacement.",
  },
  {
    id: "promotion",
    type: "play",
    title: "Étape 4 — Devenir Ari'i",
    instruction:
      "Atteignez un coin du camp adverse pour devenir Ari'i (roi). Déplacez le pion en (2, 3) vers le coin (4, 4).",
    getBoard: () => {
      const b = emptyBoard();
      b[3][2] = { owner: "A", king: false };
      return b;
    },
    turn: "A",
    validateMove: (m) => moveMatches(m, [2, 3], [4, 4]),
    success: "Vous êtes Ari'i ! Le roi se déplace dans les 8 directions et glisse sur plusieurs cases.",
  },
  {
    id: "win",
    type: "info",
    title: "Gagner la partie",
    instruction:
      "Vous gagnez si vous capturez tous les pions adverses ou si l'adversaire ne peut plus bouger. En partie locale, la règle du Tapea permet de « souffler » un pion qui oublie une capture.",
  },
  {
    id: "done",
    type: "info",
    title: "Prêt à jouer !",
    instruction:
      "Entraînez-vous en mode Facile contre l'IA (conseils activés), puis passez en Normal quand vous êtes à l'aise. Ia ora na et bon jeu !",
  },
];

export function getTutorialStep(index) {
  return TUTORIAL_STEPS[index] ?? null;
}

export function tutorialStepCount() {
  return TUTORIAL_STEPS.length;
}
